import { useSuspenseQuery } from '@tanstack/react-query';
import React from 'react';

import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import {
    Button,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    DialogRoot,
    DialogTrigger,
    Heading,
    LabeledContainer,
    LabeledContainerActionableTitle,
    LabeledContainerBody,
    LabeledContainerHeader,
    LoadingPanel,
    RevealableToolbar,
    RevealableToolbarItem,
    RevealableToolbarRoot,
} from '@gitbook/spine-react';

import { apiQuery } from '../../state';
import { BasicDocument } from '../BasicEditor';
import { ConversationDialog } from './ConversationDialog';
import { ConversationSourceTileIcon } from './ConversationSourceIcon';
import { ConversationSubject } from './ConversationSubject';
import { ConversationsIssuesClusterDialog } from './ConversationsIssuesClusterDialog';

export type ConversationIssueDialogProps = {
    organizationId: string;
    issueId: string;

    /** If false, the link to the cluster will not be displayed */
    withClusterRelation?: boolean;

    /** If false, the link to the conversation will not be displayed */
    withConversationRelation?: boolean;
};

/**
 * Dialog to inspect a conversation issue.
 */
export function ConversationIssueDialog(props: ConversationIssueDialogProps) {
    return (
        <Dialog>
            <React.Suspense fallback={<LoadingPanel />}>
                <ConversationIssueDialogContent {...props} />
            </React.Suspense>
        </Dialog>
    );
}

function ConversationIssueDialogContent(props: ConversationIssueDialogProps) {
    const {
        organizationId,
        issueId,
        withClusterRelation = true,
        withConversationRelation = true,
    } = props;

    const {
        data: { issue, conversation },
    } = useSuspenseQuery(apiQuery('getConversationIssueById', { organizationId, issueId }));

    return (
        <>
            <DialogHeader>
                <Heading>{issue.title}</Heading>
            </DialogHeader>
            <DialogBody>
                <div className="flex flex-col gap-6">
                    <LabeledContainer>
                        <LabeledContainerHeader>
                            <LabeledContainerActionableTitle>
                                <Heading>Summary</Heading>
                            </LabeledContainerActionableTitle>
                        </LabeledContainerHeader>
                        <LabeledContainerBody>
                            <BasicDocument document={issue.details} />
                        </LabeledContainerBody>
                    </LabeledContainer>
                    <LabeledContainer>
                        <LabeledContainerHeader>
                            <LabeledContainerActionableTitle>
                                <Heading>Extracted from</Heading>
                            </LabeledContainerActionableTitle>
                        </LabeledContainerHeader>
                        <LabeledContainerBody>
                            <RevealableToolbarRoot>
                                <div className="flex items-center gap-2">
                                    <ConversationSourceTileIcon
                                        source={conversation.source}
                                        size={16}
                                    />
                                    <Description>
                                        <ConversationSubject conversation={conversation} />{' '}
                                        <DateShortRelative
                                            value={conversation.createdAt}
                                            onlyDate={false}
                                        />
                                    </Description>
                                </div>
                                {withConversationRelation ? (
                                    <RevealableToolbar aria-label="Actions">
                                        <RevealableToolbarItem>
                                            <DialogRoot>
                                                <DialogTrigger>
                                                    <Button
                                                        kind="blank"
                                                        size="small"
                                                        label="Inspect the conversation"
                                                        leadingIcon={Icon.Maximize}
                                                        iconOnly
                                                    />
                                                </DialogTrigger>
                                                <ConversationDialog
                                                    organizationId={organizationId}
                                                    conversationId={conversation.id}
                                                    withClusterRelation={withClusterRelation}
                                                />
                                            </DialogRoot>
                                        </RevealableToolbarItem>
                                    </RevealableToolbar>
                                ) : null}
                            </RevealableToolbarRoot>
                        </LabeledContainerBody>
                    </LabeledContainer>
                    {withClusterRelation ? (
                        <LabeledContainer>
                            <LabeledContainerHeader>
                                <LabeledContainerActionableTitle>
                                    <Heading>Identified as</Heading>
                                </LabeledContainerActionableTitle>
                            </LabeledContainerHeader>
                            <LabeledContainerBody>
                                {issue.cluster ? (
                                    <ConversationsIssuesClusterDetails
                                        organizationId={organizationId}
                                        clusterId={issue.cluster}
                                    />
                                ) : (
                                    <Description>
                                        No cluster of issues has been identified for this issue yet.
                                        The GitBook Agent will require more similar issues to
                                        identify a cluster.
                                    </Description>
                                )}
                            </LabeledContainerBody>
                        </LabeledContainer>
                    ) : null}
                </div>
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton label="Close" />
            </DialogFooter>
        </>
    );
}

function ConversationsIssuesClusterDetails(props: { organizationId: string; clusterId: string }) {
    const { organizationId, clusterId } = props;

    const { data: cluster } = useSuspenseQuery(
        apiQuery('getConversationsIssuesCluster', { organizationId, clusterId })
    );

    return (
        <RevealableToolbarRoot>
            <Description>{cluster.label}</Description>
            <RevealableToolbar aria-label="Actions">
                <RevealableToolbarItem>
                    <DialogRoot>
                        <DialogTrigger>
                            <Button
                                kind="blank"
                                size="small"
                                label="Inspect the cluster"
                                leadingIcon={Icon.Maximize}
                                iconOnly
                            />
                        </DialogTrigger>
                        <ConversationsIssuesClusterDialog
                            organizationId={organizationId}
                            clusterId={clusterId}
                        />
                    </DialogRoot>
                </RevealableToolbarItem>
            </RevealableToolbar>
        </RevealableToolbarRoot>
    );
}
